import { useTranslation } from 'react-i18next'
import { useContext, useState, useRef, useEffect } from 'react'

import Dialog from '../common/dialog'
import { BankingPartner } from '../../types/user.types'
import { requestAccessToken } from '../../api/auth.api'
import { handleGetIfEmailIsValid } from '../../utils/common.utils'
import { AuthContext, NotificationsContext } from '../../contexts'
import { getCurrentSystemUsersUsernames } from '../../api/app-queries.api'
import { handleSyncDataToLocalDB, normalizeCustomerCompanyData } from '../../utils'
import { NewCustomerCompanyFormStepOne } from './new-customer-company-from-step-one'
import { NewCustomerCompanyProcessStepper } from './new-customer-company-process-stepper'
import { customerCompanyOnboarding, updateCustomerCompanyOnboarding } from '../../api/customers.api'
import { CustomerCompanyFeesData, NewCustomerCompanyFormBankingPartners } from './new-customer-company-form-banking-partners'

export type CompanyOnboardingData = {
  businessName: string
  documentType: string
  documentNumber: string
  language: string
  companyMainUserData: {
    email?: string
    lastName?: string
    firstName?: string
  }
}

export const AddNewCustomerCompanyModal = ({
  customerCompanyId,
  handleCloseSession,
  availableCurrencies,
  handleOpenStateOfModal,
  currentBankingPartners,
  customerCompanyDataToUpdate,
  bankingPartnersDataToUpdate,
  handleGetCurrentBankingPartners,
  handleAddCustomerCompanyDataToTable
}: {
  customerCompanyId?: string
  handleCloseSession: () => void
  availableCurrencies: Array<string>
  handleGetCurrentBankingPartners?: () => void
  currentBankingPartners: Array<BankingPartner>
  handleOpenStateOfModal: (openState: boolean) => void
  customerCompanyDataToUpdate?: CompanyOnboardingData | null
  bankingPartnersDataToUpdate: Array<CustomerCompanyFeesData>
  handleAddCustomerCompanyDataToTable?: (customerCompanyDataToAddToTable: any) => void
}) => {
  const { t } = useTranslation(['customer-companies-management', 'customer-company-details-management', 'common'])
  const { updateNewNotificationStatus } = useContext(NotificationsContext)

  const { isAuth } = useContext(AuthContext)
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [customerCompanyData, setCustomerCompanyData] = useState<CompanyOnboardingData>()
  const [bankingPartnersData, setBankingPartnersData] = useState<Array<CustomerCompanyFeesData>>([])
  const [dialogNewCustomerCompanyIsLoading, setDialogNewCustomerCompanyIsLoading] = useState<boolean>(false)

  const newClientFormStepOneFormRef = useRef<{ companyOnboardingData: CompanyOnboardingData; handleSetCompanyOnboardingData: (companyOnboardingDataToSet: CompanyOnboardingData, isUpdate?: boolean) => void }>()
  const newClientFormStepBankingPartnersFormRef = useRef<{
    customerCompanyBankingPartnersData: Array<CustomerCompanyFeesData>
    handleSetBankingPartnersData: (bankingPartnersDataToSet: Array<CustomerCompanyFeesData>) => void
  }>()

  useEffect(() => {
    if (customerCompanyDataToUpdate && bankingPartnersDataToUpdate.length > 0) {
      newClientFormStepOneFormRef.current?.handleSetCompanyOnboardingData(customerCompanyDataToUpdate, true)
      setCustomerCompanyData(customerCompanyDataToUpdate)
      setBankingPartnersData(bankingPartnersDataToUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCompanyDataToUpdate])

  const handleValidateSecondStepForm = async () => {
    const errorsData: { [key: string]: string } = {}
    const customerCompanyBankingPartnersData = newClientFormStepBankingPartnersFormRef?.current?.customerCompanyBankingPartnersData

    for (const [index, bankingPartnerData] of (customerCompanyBankingPartnersData as unknown as Array<CustomerCompanyFeesData>).entries()) {
      if (!bankingPartnerData.fee) {
        errorsData[`feeError-${index}`] = t('newClientModal.errors.dataMissing')
      }

      for (const [additionalIndex, bankingPartnerAdditionalData] of bankingPartnerData.additionalData.entries()) {
        if (bankingPartnerData.allowedToOperate === true && !bankingPartnerAdditionalData.accountId) {
          errorsData[`additionalData-${index}-${additionalIndex}`] = t('newClientModal.errors.dataMissing')
        }
      }
    }

    if (Object.keys(errorsData).length > 0) {
      setErrors(errorsData)
      return
    }

    try {
      setErrors({})
      setBankingPartnersData(customerCompanyBankingPartnersData as unknown as Array<CustomerCompanyFeesData>)

      setDialogNewCustomerCompanyIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')

      if (!customerCompanyId) {
        const {
          data: { currentSystemUsersUsernames, currentCustomerCompaniesDocuments }
        } = await getCurrentSystemUsersUsernames(accessTokenData.data.accessToken)

        const systemUserAlreadyExists = currentSystemUsersUsernames.find(
          (systemUserUsername: { username: string }) => systemUserUsername.username.toLowerCase() === customerCompanyData?.companyMainUserData.email?.toLowerCase()
        )

        const systemUserAlreadyExistsByDocumentNumber = currentCustomerCompaniesDocuments.find(
          (customerCompanyDocument: { businessCompany: { documentNumber: string } }) =>
            customerCompanyDocument.businessCompany.documentNumber.replaceAll(/[^a-zA-Z0-9]/g, '') === customerCompanyData?.documentNumber.replaceAll(/[^a-zA-Z0-9]/g, '')
        )

        if (systemUserAlreadyExists || systemUserAlreadyExistsByDocumentNumber) {
          setCurrentStep(1)
          setTimeout(() => {
            newClientFormStepOneFormRef.current?.handleSetCompanyOnboardingData(customerCompanyData as CompanyOnboardingData)
          }, 1)
          const errorsToSet: { [key: string]: string } = {}

          if (systemUserAlreadyExists) {
            errorsToSet.companyMainUserDataEmail = t('customerCompanyManageModal.complianceUsersModal.errors.alreadyExists', { ns: 'customer-company-details-management' })
          }

          if (systemUserAlreadyExistsByDocumentNumber) {
            errorsToSet.documentNumber = t('customerCompanyManageModal.complianceUsersModal.errors.alreadyExistsByDocumentNumber', { ns: 'customer-company-details-management' })
          }

          setErrors(errorsToSet)
          setDialogNewCustomerCompanyIsLoading(false)
          return
        }
      }

      const createdCustomerCompanyData = customerCompanyId
        ? await updateCustomerCompanyOnboarding(accessTokenData.data.accessToken, customerCompanyId, customerCompanyData, customerCompanyBankingPartnersData)
        : await customerCompanyOnboarding(accessTokenData.data.accessToken, customerCompanyData, customerCompanyBankingPartnersData)
      const customerCompanyToAdd = customerCompanyId
        ? normalizeCustomerCompanyData(createdCustomerCompanyData.data.customerCompanyUpdated)
        : normalizeCustomerCompanyData(createdCustomerCompanyData.data.newCustomerCompany)
      await handleSyncDataToLocalDB('customer-companies', customerCompanyToAdd)

      if (customerCompanyId) {
        handleOpenStateOfModal(false)
        setDialogNewCustomerCompanyIsLoading(false)
        handleGetCurrentBankingPartners?.()
        updateNewNotificationStatus?.(true, 'customer-company-updated')
        return
      }

      handleAddCustomerCompanyDataToTable?.({ ...customerCompanyToAdd, selected: false })

      handleOpenStateOfModal(false)
      setCustomerCompanyData(undefined)
      setDialogNewCustomerCompanyIsLoading(false)
      updateNewNotificationStatus?.(true, 'customer-company-created')
    } catch (error: any) {
      console.log(error, error?.response, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.data?.message === 'Not authorized: Exists') {
        setErrors({ generalError: t('formGeneralErrors.exists', { ns: 'common' }) })
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 403) {
        const bankingPartnerIndex = customerCompanyBankingPartnersData?.findIndex((bankingPartnerData) => bankingPartnerData.currency === error?.response.data.currency)

        errorsData[`checkError-${bankingPartnerIndex}`] = t('newClientModal.errors.dataNotAllowedToDeny')
        setErrors({ ...errorsData, generalError: t(`formGeneralErrors.${error?.response.data.message}`, { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogNewCustomerCompanyIsLoading(false)
    }
  }

  const handleGoToStep = (stepToGo: number) => {
    if (currentStep === 1 && stepToGo === 2) {
      const isFirstStepCompleted = handleValidateFirstStepForm()

      if (isFirstStepCompleted === false) {
        return
      }

      setCustomerCompanyData({
        language: String(newClientFormStepOneFormRef.current?.companyOnboardingData.language),
        documentType: String(newClientFormStepOneFormRef.current?.companyOnboardingData.documentType),
        documentNumber: String(newClientFormStepOneFormRef.current?.companyOnboardingData.documentNumber),
        businessName: String(newClientFormStepOneFormRef.current?.companyOnboardingData.businessName.toUpperCase()),
        companyMainUserData: {
          email: String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.email?.toLowerCase()),
          lastName: String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.lastName?.toUpperCase()),
          firstName: String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.firstName?.toUpperCase())
        }
      })

      setCurrentStep(2)
      if (bankingPartnersData.length > 0) {
        setTimeout(() => {
          newClientFormStepBankingPartnersFormRef.current?.handleSetBankingPartnersData(bankingPartnersData)
        }, 1)
      }

      return
    }

    if (currentStep === 2 && stepToGo === 1) {
      if (bankingPartnersData.length > 0) {
        newClientFormStepBankingPartnersFormRef.current?.handleSetBankingPartnersData(bankingPartnersData)
      }

      setCurrentStep(1)
      setTimeout(() => {
        newClientFormStepOneFormRef.current?.handleSetCompanyOnboardingData(customerCompanyData as CompanyOnboardingData)
      }, 1)
    }
  }

  const handleValidateFirstStepForm = () => {
    setErrors({})
    let formIsCompleted = true
    const errorsToSet = {
      language: '',
      businessName: '',
      documentType: '',
      documentNumber: '',
      companyMainUserDataEmail: '',
      companyMainUserDataLastName: '',
      companyMainUserDataFirstName: ''
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.businessName) {
      formIsCompleted = false
      errorsToSet.businessName = t('newClientForm.formStepThree.errors.businessCompany.name')
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData?.firstName) {
      formIsCompleted = false
      errorsToSet.companyMainUserDataFirstName = t('newClientForm.formStepOne.errors.businessOwner.firstName')
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData?.lastName) {
      formIsCompleted = false
      errorsToSet.companyMainUserDataLastName = t('newClientForm.formStepOne.errors.businessOwner.lastName')
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.documentType) {
      formIsCompleted = false
      errorsToSet.documentType = t('newClientForm.formStepOne.errors.businessOwner.documentType')
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.documentNumber) {
      formIsCompleted = false
      errorsToSet.documentNumber = t('newClientForm.formStepOne.errors.businessOwner.documentNumber')
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.language) {
      formIsCompleted = false
      errorsToSet.language = t('newClientForm.formStepOne.errors.businessOwner.language')
    }

    if (!newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData?.email) {
      formIsCompleted = false
      errorsToSet.companyMainUserDataEmail = t('newClientForm.formStepThree.errors.businessCompany.email')
    }

    if (newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData?.email && !handleGetIfEmailIsValid(String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.email))) {
      formIsCompleted = false
      errorsToSet.companyMainUserDataEmail = t('newClientForm.formStepThree.errors.businessCompany.invalidEmail')
    }

    if (!formIsCompleted) {
      setErrors(errorsToSet)
      return false
    }

    return true
  }

  const handleGoToNextStep = () => {
    const isFirstStepCompleted = handleValidateFirstStepForm()

    if (isFirstStepCompleted === false) {
      return
    }

    setCustomerCompanyData({
      language: String(newClientFormStepOneFormRef.current?.companyOnboardingData.language),
      documentType: String(newClientFormStepOneFormRef.current?.companyOnboardingData.documentType),
      documentNumber: String(newClientFormStepOneFormRef.current?.companyOnboardingData.documentNumber),
      businessName: String(newClientFormStepOneFormRef.current?.companyOnboardingData.businessName.toUpperCase()),
      companyMainUserData: {
        email: String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.email?.toLowerCase()),
        lastName: String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.lastName?.toUpperCase()),
        firstName: String(newClientFormStepOneFormRef.current?.companyOnboardingData.companyMainUserData.firstName?.toUpperCase())
      }
    })

    setCurrentStep(2)
    if (bankingPartnersData.length > 0) {
      setTimeout(() => {
        newClientFormStepBankingPartnersFormRef.current?.handleSetBankingPartnersData(bankingPartnersData)
      }, 1)
    }
  }

  return (
    <Dialog
      errors={errors}
      maxHeight="865px"
      dialogIsLoading={dialogNewCustomerCompanyIsLoading}
      handleDialogAction={currentStep < 2 ? handleGoToNextStep : handleValidateSecondStepForm}
      dialogTitle={customerCompanyDataToUpdate ? t('updateClientModal.title') : t('newClientModal.title')}
      actionButtonText={
        customerCompanyDataToUpdate && currentStep < 2
          ? t('updateClientModal.nextStep')
          : customerCompanyDataToUpdate && currentStep === 2
          ? t('updateClientModal.actionButtonText')
          : !customerCompanyDataToUpdate && currentStep < 2
          ? t('newClientModal.nextStep')
          : t('newClientModal.actionButtonText')
      }
      handleDialogOpenState={(openState: boolean) => {
        handleOpenStateOfModal(openState)

        if (openState === false) {
          setErrors({})
          setCustomerCompanyData(undefined)
        }
      }}
    >
      <NewCustomerCompanyProcessStepper
        currentStep={currentStep}
        handleGoToStep={handleGoToStep}
      />
      <div className="new-client-form">
        {currentStep === 1 && (
          <NewCustomerCompanyFormStepOne
            errors={errors}
            ref={newClientFormStepOneFormRef}
          />
        )}
        {currentStep === 2 && (
          <NewCustomerCompanyFormBankingPartners
            errors={errors}
            availableCurrencies={availableCurrencies}
            ref={newClientFormStepBankingPartnersFormRef}
            currentBankingPartners={currentBankingPartners}
          />
        )}
      </div>
    </Dialog>
  )
}
